import { BFN_URL } from "@/config/bfnConfig";

import { ApiEndpoints } from "../apiEndpoints";
import { requestApi } from "../apiService";

export interface BfnTokenResponse {
  token: string;
  expiresAt: string;
}

/**
 * Thrown when the entitlement check at the BE token endpoint returns 403
 * (`{ error: 'bfn_access_denied' }`). The user is simply not entitled — the UI
 * shows an upsell/info state rather than a hard error.
 */
export class BfnAccessDeniedError extends Error {
  constructor() {
    super("bfn_access_denied");
    this.name = "BfnAccessDeniedError";
  }
}

/**
 * Exchange the current OA bearer token for a short-lived BFN token.
 *
 * Goes through the shared apiService (bearer auto-injected, 401 refresh). The
 * 403 body is not AppError-shaped, so apiService surfaces it as a generic
 * Error whose message carries the sentinel — we translate that into a typed
 * {@link BfnAccessDeniedError} so callers can branch on entitlement cleanly.
 */
export const exchangeBfnToken = async (): Promise<BfnTokenResponse> => {
  try {
    const response = await requestApi({
      url: ApiEndpoints.BFN_TOKEN,
      method: "POST",
    });
    return response.data as BfnTokenResponse;
  } catch (error) {
    if (error instanceof Error && error.message.includes("bfn_access_denied")) {
      throw new BfnAccessDeniedError();
    }
    throw error;
  }
};

/**
 * Establish the gateway cookie session.
 *
 * Cross-origin fetch with `credentials: 'include'` so the gateway can set its
 * `__Host-` session cookie; the cookie then rides the iframe's own requests.
 * The OA token never enters the iframe's JS context — only this scoped,
 * short-TTL bfnToken is posted to the gateway.
 */
export const establishBfnSession = async (bfnToken: string): Promise<void> => {
  // Fail closed: never fall back to a same-origin relative POST when the
  // gateway origin is unset/invalid — that would leak the bfn token to the OA
  // app's own origin. Callers gate on BFN_AVAILABLE, but guard here too.
  if (!BFN_URL) {
    throw new Error("BFN gateway origin is not configured");
  }
  const response = await fetch(`${BFN_URL}/session`, {
    method: "POST",
    mode: "cors",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token: bfnToken }),
  });
  if (!response.ok) {
    throw new Error(`BFN session handshake failed: ${response.status}`);
  }
};
